/*
  Vanhess first-touch attribution capture.

  Records how a visitor reached the site on their first page view, keeps it in
  localStorage, and copies it onto the Shopify cart as attributes so it lands on
  the order and shows in the admin under Additional details.

  What this can and cannot capture:

  - Google removed the search term from organic referrers in 2011 and finished
    the job in 2013. The keyword behind an organic click is not recoverable by
    any script. Do not add code here that claims to recover it.
  - What is recoverable: the landing page, the referring site, UTM tags, the
    paid click id, and anything the visitor typed into the site's own search box.
  - For Google Ads clicks the gclid captured here can be resolved to the real
    search term later through the Google Ads API. See
    scripts/attribution-report.py for that join.

  Attribute names start with a single underscore. Shopify hides those from the
  customer at checkout while still showing them to staff on the order page.
*/
(function () {
  'use strict';

  var FIRST_KEY = 'vh_attr_first_v1';
  var LAST_KEY = 'vh_attr_last_v1';
  var SEARCH_KEY = 'vh_attr_searches_v1';
  var VISITS_KEY = 'vh_attr_visits_v1';
  var SYNC_KEY = 'vh_attr_synced_v1';
  var SESSION_KEY = 'vh_attr_session_v1';

  // Self-imposed caps so a long URL cannot bloat every cart. Not Shopify limits.
  var MAX_VALUE = 255;
  var MAX_SEARCHES = 5;
  var MAX_SEARCH_LEN = 60;

  // Checked in order. AI assistants must come before search engines, otherwise
  // gemini.google.com is swallowed by the google rule.
  var AI = [
    ['chatgpt.com', 'ChatGPT'],
    ['chat.openai.com', 'ChatGPT'],
    ['openai.com', 'ChatGPT'],
    ['perplexity.ai', 'Perplexity'],
    ['claude.ai', 'Claude'],
    ['gemini.google.com', 'Gemini'],
    ['copilot.microsoft.com', 'Copilot']
  ];
  var SEARCH = [
    ['google.', 'Google organic'],
    ['bing.com', 'Bing organic'],
    ['duckduckgo.com', 'DuckDuckGo'],
    ['yahoo.', 'Yahoo'],
    ['ecosia.org', 'Ecosia'],
    ['search.brave.com', 'Brave']
  ];
  var SOCIAL = [
    ['instagram.com', 'Instagram'],
    ['facebook.com', 'Facebook'],
    ['pinterest.', 'Pinterest'],
    ['tiktok.com', 'TikTok'],
    ['reddit.com', 'Reddit'],
    ['youtube.com', 'YouTube'],
    ['linkedin.com', 'LinkedIn'],
    ['x.com', 'X'],
    ['twitter.com', 'X']
  ];
  var CLICK_IDS = [
    ['gclid', 'Google Ads'],
    ['gbraid', 'Google Ads'],
    ['wbraid', 'Google Ads'],
    ['msclkid', 'Microsoft Ads'],
    ['fbclid', 'Meta Ads'],
    ['ttclid', 'TikTok Ads']
  ];

  function ls() {
    try {
      var s = window.localStorage;
      s.setItem('vh_t', '1');
      s.removeItem('vh_t');
      return s;
    } catch (e) {
      return null;
    }
  }

  function ss() {
    try {
      return window.sessionStorage;
    } catch (e) {
      return null;
    }
  }

  function readJSON(store, key) {
    if (!store) return null;
    try {
      return JSON.parse(store.getItem(key));
    } catch (e) {
      return null;
    }
  }

  function writeJSON(store, key, value) {
    if (!store) return;
    try {
      store.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* quota or private mode, attribution is best effort */
    }
  }

  function trim(value) {
    if (!value) return '';
    value = String(value);
    return value.length > MAX_VALUE ? value.slice(0, MAX_VALUE) : value;
  }

  // Pacific, because this string is read by staff on the order page in admin.
  function ptStamp() {
    try {
      return new Date().toLocaleString('en-CA', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).replace(',', '') + ' PT';
    } catch (e) {
      return new Date().toISOString() + ' UTC';
    }
  }

  function params() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (e) {
      return null;
    }
  }

  function match(host, table) {
    for (var i = 0; i < table.length; i++) {
      if (host.indexOf(table[i][0]) !== -1) return table[i][1];
    }
    return null;
  }

  function referrerHost() {
    var ref = document.referrer || '';
    if (!ref) return '';
    try {
      var host = new URL(ref).hostname;
      return host === window.location.hostname ? '' : host;
    } catch (e) {
      return '';
    }
  }

  function clickId(p) {
    if (!p) return null;
    for (var i = 0; i < CLICK_IDS.length; i++) {
      var value = p.get(CLICK_IDS[i][0]);
      if (value) return { key: CLICK_IDS[i][0], value: value, label: CLICK_IDS[i][1] };
    }
    return null;
  }

  // Precedence: explicit campaign tags, then paid click id, then referrer.
  function classify(p, host) {
    if (p) {
      var utmSource = p.get('utm_source');
      if (utmSource) {
        var utmMedium = p.get('utm_medium');
        return utmMedium ? utmSource + ' / ' + utmMedium : utmSource;
      }
    }
    var click = clickId(p);
    if (click) return click.label;
    if (!host) return 'Direct';
    return match(host, AI) || match(host, SEARCH) || match(host, SOCIAL) || host;
  }

  function landingPage() {
    return window.location.pathname + (window.location.search || '');
  }

  function countVisit(store, session) {
    var visits = parseInt((store && store.getItem(VISITS_KEY)) || '0', 10) || 0;
    // One increment per browser session, not per page view.
    if (session && !session.getItem(SESSION_KEY)) {
      visits += 1;
      try {
        session.setItem(SESSION_KEY, '1');
        store.setItem(VISITS_KEY, String(visits));
      } catch (e) {
        /* ignore */
      }
    }
    return visits || 1;
  }

  function recordSearch(store) {
    var p = params();
    if (!p) return;
    var term = p.get('q');
    if (!term || window.location.pathname.indexOf('/search') === -1) return;

    term = term.trim().slice(0, MAX_SEARCH_LEN);
    if (!term) return;

    var list = readJSON(store, SEARCH_KEY) || [];
    var seen = list.some(function (existing) {
      return existing.toLowerCase() === term.toLowerCase();
    });
    if (seen) return;

    list.push(term);
    if (list.length > MAX_SEARCHES) list = list.slice(list.length - MAX_SEARCHES);
    writeJSON(store, SEARCH_KEY, list);
  }

  function cartUpdateUrl() {
    var base = '/cart';
    if (window.theme && window.theme.routes && window.theme.routes.cart_url) {
      base = window.theme.routes.cart_url;
    }
    return base.replace(/\/$/, '') + '/update.js';
  }

  function post(attributes) {
    if (!window.fetch) return null;
    return window.fetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).catch(function () {
      /* never let attribution break the storefront */
    });
  }

  function sync(store, attributes) {
    var signature = JSON.stringify(attributes);
    if (store && store.getItem(SYNC_KEY) === signature) return;

    /*
      Record the write before sending it, so a visitor moving quickly through
      several pages does not POST the same attributes on every one. If the
      request fails the marker is removed again and the next page view retries.
    */
    try {
      if (store) store.setItem(SYNC_KEY, signature);
    } catch (e) {
      /* ignore */
    }

    var request = post(attributes);
    if (!request) return;

    request.then(function (response) {
      if (store && (!response || !response.ok)) {
        try {
          store.removeItem(SYNC_KEY);
        } catch (e) {
          /* ignore */
        }
      }
    });
  }

  /*
    The cart form only submits the "How did you find us" answer when the customer
    clicks the theme's own checkout button. Shop Pay and the other accelerated
    buttons skip the form, so save the answer the moment it is chosen.
  */
  function bindFoundUs() {
    var select = document.querySelector('[data-vh-found-us]');
    if (!select) return;

    select.addEventListener('change', function () {
      var key = select.getAttribute('data-vh-found-us');
      if (!key) return;
      var payload = {};
      payload[key] = trim(select.value);
      post(payload);
    });
  }

  function run() {
    // Do not write carts from the theme editor preview.
    if (window.Shopify && window.Shopify.designMode) return;

    bindFoundUs();

    var store = ls();
    if (!store) return;

    var session = ss();
    var p = params();
    var host = referrerHost();

    var first = readJSON(store, FIRST_KEY);
    if (!first) {
      first = {
        source: classify(p, host),
        landing: landingPage(),
        referrer: document.referrer || '',
        seen: ptStamp()
      };
      var click = clickId(p);
      if (click) first.click = click.key + '=' + click.value;
      writeJSON(store, FIRST_KEY, first);
    }

    // Last touch only moves when the visitor arrives from somewhere external.
    if (host || (p && p.get('utm_source'))) {
      writeJSON(store, LAST_KEY, { source: classify(p, host), landing: landingPage() });
    }
    var last = readJSON(store, LAST_KEY) || first;

    recordSearch(store);

    var visits = countVisit(store, session);
    var searches = readJSON(store, SEARCH_KEY) || [];

    var attributes = {
      _vh_first_source: trim(first.source),
      _vh_first_landing: trim(first.landing),
      _vh_first_referrer: trim(first.referrer),
      _vh_first_seen: trim(first.seen),
      _vh_last_source: trim(last.source),
      _vh_visits: String(visits)
    };
    if (first.click) attributes._vh_click_id = trim(first.click);
    if (searches.length) attributes._vh_site_searches = trim(searches.join(' | '));

    sync(store, attributes);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
